<script setup lang="ts">
import type { RouteRecordNormalized } from 'vue-router';
import { useDebounceFn } from '@vueuse/core';
import type { SearchResult, SearchResultType, SearchResultGroup, SearchResultGroupValue } from '../../../../runtime/services/SearchService';
import SearchService from '../../../../runtime/services/SearchService';

const loading = defineModel<boolean>('loading');
const results = defineModel<boolean>('results');
const props = withDefaults(defineProps<{
    query?: string;
}>(), {
    query: ''
});
const emit = defineEmits<{
    (e: 'close'): void;
}>();

const close = () =>
{
    emit('close');
};

const router = useRouter();
const { $t } = useLocalization();
const { $log } = useLogging();

const routes = ref<string[]>([]);
const redirects = ref<Record<string, string>>({});

const onlyMine = ref(false);
const allVersions = ref(false);
const pager = ref<any>(new Pager(1, 10));
const items = ref<SearchResult[]>([]);

const types = ref<NodeItem<string, SearchResultType>[]>([]);
const typeNodes = computed<any>(() => types.value);
const expandedTypes = ref<string[]>([null]);
const selectedTypes = ref<string[]>([]);

const groups = ref<NodeItem<string, SearchResultGroupValue>[]>([]);
const groupNodes = computed<any>(() => groups.value);
const expandedGroups = ref<string[]>([null]);
const selectedValues = ref<string[]>([]);

const hasResults = computed(() => items.value.length > 0 || types.value.length > 0);

onMounted(async () =>
{
    router.getRoutes().forEach(prepareRoutes);
});

const prepareRoutes = (route: RouteRecordNormalized): void =>
{
    const params = route.path.split('/').filter(p => p.startsWith(':') && !p.endsWith('?'));

    if (route.name && params.length === 0)
    {
        routes.value.push(route.name.toString());
    }

    if (route.meta?.model)
    {
        redirects.value[route.meta.model as string] = route.name as string;
    }
};

const routeUrl = (result: SearchResult): any =>
{
    return {
        name: redirects.value[result.type] ?? 'dashboard',
        params: { id: result.id }
    };
};

const clear = (): void =>
{
    onlyMine.value = false;
    allVersions.value = false;
    items.value = [];

    types.value = [];
    expandedTypes.value = [null];
    selectedTypes.value = [];

    groups.value = [];
    expandedGroups.value = [null];
    selectedValues.value = [];
};

const performSearch = useDebounceFn(async (): Promise<void> =>
{
    try
    {
        if (props.query.length >= 3)
        {
            const response = await SearchService.getSearchResults(
                props.query,
                selectedTypes.value,
                selectedValues.value.map(item => item.split(':') as [string, string]),
                onlyMine.value,
                allVersions.value,
                pager.value
            );

            pager.value.setTotalRows(response.totalRows);
            items.value = response.items;
            prepareTypes(response.meta.types ?? []);
            prepareGroups(response.meta.groups ?? []);

            results.value = items.value.length > 0;
        }
    }
    catch (ex)
    {
        pager.value.setTotalRows(0);
        items.value = [];
        $log.error(ex);
    }
    finally
    {
        loading.value = false;
    }
},
500);

const prepareTypes = (typesData: SearchResultType[]): void =>
{
    types.value = [];
    expandedTypes.value = [null];

    typesData.forEach((p) =>
    {
        const path = p.name.split(' / ');
        const nodes = path.map((q, i) => ({
            id: path.slice(0, i + 1).join(' / '),
            parentId: i > 0 ? path.slice(0, i).join(' / ') : null,
            hasChildren: false,
            resource: { ...p, name: q }
        }));

        nodes.forEach((n, i) =>
        {
            if (i < nodes.length - 1)
            {
                n.hasChildren = true;
                n.resource.count = 0;
                n.resource.key = n.parentId;
            }

            if (!expandedTypes.value.includes(n.parentId))
            {
                expandedTypes.value.push(n.parentId);
            }

            if (types.value.findIndex(p => p.id === n.id) === -1)
            {
                types.value.push(n as NodeItem<string, SearchResultType>);
            }
        });
    });
};

const prepareGroups = (groupsData: SearchResultGroup[]): void =>
{
    groups.value = [];
    expandedGroups.value = [null];

    groupsData.forEach((group) =>
    {
        if (group.values.length > 0)
        {
            expandedGroups.value.push(group.key);

            groups.value.push({
                id: group.key,
                parentId: null,
                hasChildren: true,
                resource: group
            });

            group.values.forEach((value) =>
            {
                groups.value.push({
                    id: value.key,
                    parentId: group.key,
                    hasChildren: false,
                    resource: value
                });
            });
        }
    });
};

watch(() => props.query, (value) =>
{
    if (value)
    {
        loading.value = true;

        if (value.length < 3)
        {
            clear();
        }
        else
        {
            performSearch();
        }
    }
});

watch(selectedTypes, () =>
{
    selectedValues.value = [];
    performSearch();
});

watch([onlyMine, allVersions, selectedValues], () =>
{
    performSearch();
});

defineExpose({
    clear
});
</script>

<template>
    <div v-if="hasResults" class="row gx-2 mt-0">
        <div class="col-12 col-md-4">
            <div class="alert alert-primary px-1 pt-2 pb-0 mb-2">
                <IdeoFormCheckbox v-model="onlyMine" :disabled="loading">
                    {{ $t('[[[Pokaż tylko moje dokumenty]]]') }}<br>
                    <small>{{ $t('[[[dodane i edytowane przeze mnie]]]') }}</small>
                </IdeoFormCheckbox>
                <IdeoFormCheckbox v-model="allVersions" :disabled="loading">
                    {{ $t('[[[Szukaj we wszystkich wersjach]]]') }}<br>
                    <small>{{ $t('[[[tylko dokumenty wersjonowane]]]') }}</small>
                </IdeoFormCheckbox>
            </div>
            <IdeoTree v-if="types.length > 0" class="alert alert-secondary ps-1 pe-2 pt-1 mb-2" :pages="typeNodes" :expanded="expandedTypes">
                <template #toggle>
                    &nbsp;
                </template>
                <template #icon>
                    &nbsp;
                </template>
                <template #default="{ node }">
                    <template v-if="node.resource.count > 0">
                        <span class="me-auto">
                            <IdeoFormCheckbox v-model="selectedTypes" class="ms-n2 mb-n2" :value="node.resource.key" :disabled="loading">
                                {{ node.resource.name }}
                            </IdeoFormCheckbox>
                        </span>
                        <span class="badge rounded-pill text-bg-secondary text-white ms-2">
                            {{ node.resource.count }}
                        </span>
                    </template>
                    <span v-else class="fw-bold">
                        {{ node.resource.name }}
                    </span>
                </template>
            </IdeoTree>
            <IdeoTree v-if="groups.length > 0" class="alert alert-secondary ps-1 pe-2 pt-1 mb-2" :pages="groupNodes" :expanded="expandedGroups">
                <template #toggle>
                                    &nbsp;
                </template>
                <template #icon>
                                    &nbsp;
                </template>
                <template #default="{ node }">
                    <template v-if="node.parentId">
                        <span class="me-auto">
                            <IdeoFormCheckbox v-model="selectedValues" class="ms-n2 mb-n2" :value="`${node.parentId}:${node.id}`" :disabled="loading">
                                {{ node.resource.name }}
                            </IdeoFormCheckbox>
                        </span>
                        <span class="badge rounded-pill text-bg-secondary text-white ms-2">
                            {{ node.resource.count }}
                        </span>
                    </template>
                    <span v-else class="fw-bold">
                        {{ node.resource.name }}
                    </span>
                </template>
            </IdeoTree>
        </div>
        <div class="col-12 col-md-8">
            <div v-if="items.length > 0">
                <RouterLink
                    v-for="(item, i) in items"
                    :key="i"
                    class="pointer d-block alert alert-light mb-2 p-2"
                    :to="routeUrl(item)"
                    @click="close"
                >
                    <div class="d-flex w-100">
                        <h5 class="mb-1 me-auto">{{ item.title }}</h5>
                        <small v-if="item.dateModifiedUtc" :title="$t('[[[Aktualizacja: {0}|||%0]]]', $filters.datetime(item.dateModifiedUtc))">
                            {{ item.dateModifiedUtc.toRelative() }}
                        </small>
                    </div>
                    <div class="d-flex w-100">
                        <small class="text-primary">{{ item.typeName }}</small>
                        <span v-if="item.attachments.length > 0" class="ms-auto">
                            <i class="fa-regular fa-paperclip-vertical" :title="$t('[[[Posiada załączniki]]]')" />
                        </span>
                    </div>
                    <div class="d-flex w-100">
                        <div v-if="item.images.length == 1" class="mt-1 me-2">
                            <IdeoImg :src="$filters.image(item.images[0].url, 'thumb')" thumbnail width="75" height="75" blank-color="#777" />
                        </div>
                        <div class="w-100">
                            <p v-if="item.description" class="mb-1">
                                {{ item.description }}
                            </p>
                            <div class="row row-cols-3 mt-1">
                                <div v-for="(attrs, key) in item.attributes" :key="key" class="col">
                                    <small>{{ key }}</small>
                                    <div>
                                        <span v-for="(attr, a) in attrs" :key="a" class="fs-6">{{ attr }}</span>
                                    </div>
                                </div>
                            </div>
                            <div v-if="item.images.length > 1" class="mt-1">
                                <span v-for="(img, j) in item.images" :key="j" class="me-1">
                                    <IdeoImg :src="$filters.image(img.url, 'thumb')" thumbnail width="35" height="35" blank-color="#777" />
                                </span>
                            </div>
                        </div>
                    </div>
                </RouterLink>
            </div>
            <div v-else-if="loading" class="d-flex justify-content-center pt-5">
                <Loader />
            </div>
            <div v-else class="d-flex justify-content-center pt-5">
                <i class="fa-solid fa-ban fa-10x text-white" />
            </div>
        </div>
    </div>
</template>
